import React from 'react';
import { Row, Col, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/**
 * ContributionSplit - Displays pension/ISA split recommendation
 * Shows Mark's priority order:
 * 1. Employer match (pension)
 * 2. ISA (up to £20k)
 * 3. Additional pension
 * 4. GIA overflow
 */

const ContributionSplit = ({ distributionResult, profile }) => {
    if (!distributionResult || !distributionResult.recommendations) {
        return null;
    }

    const {
        recommendations,
        employer_match_captured = 0,
        employer_match_available = 0,
        employer_match_missed = 0,
        total_monthly_contribution = 0,
        total_annual_contribution = 0,
    } = distributionResult;

    // Current contributions from profile
    const currentEmployeePct = profile?.contribution?.employeeContribution || 0;
    const currentEmployerPct = profile?.contribution?.employerContribution || 0;
    const salary = profile?.financial?.annualSalary || 0;
    const currentEmployeeMonthly = salary > 0 ? Math.round((salary * currentEmployeePct / 100) / 12) : 0;
    const currentEmployerMonthly = salary > 0 ? Math.round((salary * currentEmployerPct / 100) / 12) : 0;
    const currentTotalMonthly = currentEmployeeMonthly + currentEmployerMonthly;

    // Recommended pension monthly (first pension wrapper)
    const pensionRec = recommendations.find(r => r.wrapper === 'pension');
    const rawRecommendedPensionMonthly = pensionRec?.monthly_amount || 0;

    // Detect nil-contribution case (engine returned £0 for all wrappers)
    // This happens when disposable income is at or below the threshold (Mark's rule, April 2026)
    const isNilContribution = total_monthly_contribution === 0 &&
        recommendations.every(r => (r.monthly_amount || 0) === 0);

    // Never-reduce rule: recommendation should never be less than current contribution
    // EXCEPT when engine explicitly recommends nil (disposable threshold)
    const recommendedPensionMonthly = isNilContribution
        ? 0
        : Math.max(rawRecommendedPensionMonthly, currentEmployeeMonthly);
    const monthlyIncrease = recommendedPensionMonthly - currentEmployeeMonthly;

    // Recommended employer contribution — derive from the wrapper distribution
    // The employer_match_captured reflects the NEW employer amount after the recommendation
    const recommendedEmployerMonthly = employer_match_captured > 0
        ? Math.round(employer_match_captured / 12)
        : currentEmployerMonthly;
    const recommendedEmployerPct = salary > 0
        ? (employer_match_captured > 0
            ? Math.round((employer_match_captured / salary) * 100)
            : currentEmployerPct)
        : 0;

    // Display totals: if wrapper distribution has totals, use those.
    // For nil-contribution, show 0. Otherwise fall back to existing contributions.
    const displayTotalMonthly = isNilContribution
        ? 0
        : (total_monthly_contribution > 0
            ? total_monthly_contribution
            : (recommendedPensionMonthly + recommendedEmployerMonthly));
    const displayTotalAnnual = isNilContribution
        ? 0
        : (total_annual_contribution > 0
            ? total_annual_contribution
            : (displayTotalMonthly * 12));

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value || 0);
    };

    // Sort by priority
    const sortedRecs = [...recommendations].sort((a, b) => a.priority - b.priority);

    // Get wrapper icon
    const getWrapperIcon = (wrapper) => {
        switch (wrapper) {
            case 'pension': return 'piggy-bank';
            case 'isa': return 'shield-alt';
            case 'gia': return 'chart-line';
            default: return 'wallet';
        }
    };

    // Get wrapper color
    const getWrapperColor = (wrapper) => {
        switch (wrapper) {
            case 'pension': return 'primary';
            case 'isa': return 'success';
            case 'gia': return 'secondary';
            default: return 'dark';
        }
    };

    return (
        <div className="contribution-split mt-4">
            <h6 className="mb-3">
                <FontAwesomeIcon icon="layer-group" className="me-2 text-primary" />
                Investment Allocation
            </h6>

            {/* Employer Match Alert */}
            {employer_match_captured > 0 && (
                <Alert variant="success" className="mb-3 py-2">
                    <FontAwesomeIcon icon="gift" className="me-2" />
                    <strong>Employer Match:</strong> Capturing {formatCurrency(employer_match_captured)}/year in free contributions!
                </Alert>
            )}

            {employer_match_missed > 0 && (
                <Alert variant="warning" className="mb-3 py-2">
                    <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
                    <strong>Missed Opportunity:</strong> You could get an additional {formatCurrency(employer_match_missed)}/year by increasing pension contributions.
                </Alert>
            )}

            {/* Nil Contribution Message (disposable below threshold) */}
            {isNilContribution && (
                <Alert variant="info" className="mb-3 py-2">
                    <FontAwesomeIcon icon="info-circle" className="me-2" />
                    <strong>No additional contribution recommended.</strong> Your disposable income is too tight to recommend pension contributions at this time.
                    {currentEmployeeMonthly > 0 && (
                        <span> Your existing contribution of {formatCurrency(currentEmployeeMonthly)}/month remains in place.</span>
                    )}
                </Alert>
            )}

            {/* Current vs Recommended Comparison — only when there IS a recommendation */}
            {!isNilContribution && currentEmployeeMonthly > 0 && (
                <div className="bg-light p-3 rounded mb-3">
                    <div className="small fw-semibold text-muted mb-2">Your Pension Contribution</div>
                    <Row className="align-items-center">
                        <Col xs={4} className="text-center">
                            <div className="text-muted small">Currently paying</div>
                            <div className="fs-5 fw-bold">{formatCurrency(currentEmployeeMonthly)}</div>
                            <div className="text-muted small">{currentEmployeePct}% of salary</div>
                        </Col>
                        <Col xs={4} className="text-center">
                            <FontAwesomeIcon icon="arrow-right" className="text-primary" />
                        </Col>
                        <Col xs={4} className="text-center">
                            <div className="text-muted small">We recommend</div>
                            <div className="fs-5 fw-bold text-primary">{formatCurrency(recommendedPensionMonthly)}</div>
                            {monthlyIncrease > 0 && (
                                <div className="small text-success">+{formatCurrency(monthlyIncrease)}/mo increase</div>
                            )}
                            {monthlyIncrease === 0 && (
                                <div className="small text-success">Your current contribution is suitable</div>
                            )}
                        </Col>
                    </Row>
                    {recommendedEmployerMonthly > 0 && (
                        <div className="text-center mt-2 small text-muted">
                            Plus employer contributes {formatCurrency(recommendedEmployerMonthly)}/month ({recommendedEmployerPct}% of salary)
                        </div>
                    )}
                </div>
            )}

            {/* Wrapper Breakdown */}
            <div className="wrapper-breakdown">
                {sortedRecs.map((rec, idx) => (
                    <div key={idx} className={`wrapper-item border-start border-4 border-${getWrapperColor(rec.wrapper)} ps-3 mb-3`}>
                        <Row className="align-items-center">
                            <Col xs={8}>
                                <div className="d-flex align-items-center">
                                    <FontAwesomeIcon
                                        icon={getWrapperIcon(rec.wrapper)}
                                        className={`me-2 text-${getWrapperColor(rec.wrapper)}`}
                                    />
                                    <div>
                                        <strong className="text-capitalize">{rec.wrapper}</strong>
                                        <div className="small text-muted">{rec.rationale}</div>
                                    </div>
                                </div>
                            </Col>
                            <Col xs={4} className="text-end">
                                <div className="fs-5 fw-bold">{formatCurrency(rec.monthly_amount)}</div>
                                <div className="small text-muted">/month</div>
                            </Col>
                        </Row>
                        {rec.warnings && rec.warnings.length > 0 && (
                            <div className="mt-2">
                                {rec.warnings.map((warning, wIdx) => (
                                    <div key={wIdx} className="small text-warning">
                                        <FontAwesomeIcon icon="info-circle" className="me-1" />
                                        {warning}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Total */}
            <div className="total-contribution bg-light p-3 rounded mt-3">
                <Row>
                    <Col xs={6}>
                        <div className="text-muted">Total Monthly</div>
                        <div className="fs-4 fw-bold">{formatCurrency(displayTotalMonthly)}</div>
                    </Col>
                    <Col xs={6} className="text-end">
                        <div className="text-muted">Total Annual</div>
                        <div className="fs-4 fw-bold">{formatCurrency(displayTotalAnnual)}</div>
                    </Col>
                </Row>
            </div>

            {/* Priority Explanation */}
            <div className="mt-3 small text-muted">
                <FontAwesomeIcon icon="info-circle" className="me-1" />
                Allocation priority: (1) Employer match first, (2) ISA up to £20k, (3) Additional pension, (4) GIA overflow
            </div>
        </div>
    );
};

export default ContributionSplit;